// ═══════════════════════════════════════════════════════════════
// Incident Hub — Dashboard
// Stats, active incidents, quick actions
// ═══════════════════════════════════════════════════════════════

import { useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Title, Text, Paper, Group, Badge, Button, SimpleGrid, Stack,
} from '../../components/gab/GabComponents';
import {
  IconPlus, IconArrowRight, IconShield, IconAlertTriangle,
  IconEye, IconArchive, IconCloudStorm, IconFlame, IconBiohazard,
  IconClipboardCheck, IconSchool, IconInfoCircle, IconDroplet,
  IconBuildingBridge, IconTornado, IconCalendar, IconAmbulance, IconConfetti,
} from '@tabler/icons-react';
import { useAuthStore } from '../../store/authStore';
import { INCIDENT_TYPE_META } from '../../types';
import { secondary, semantic } from '../../lib/colors';

const STATUS_COLORS: Record<string, string> = {
  active: semantic.warning,
  monitoring: semantic.info,
  closed: semantic.success,
  archived: secondary[700],
};

const ICON_MAP: Record<string, React.ComponentType<{ size?: number; color?: string }>> = {
  IconCloudStorm, IconFlame, IconBiohazard, IconClipboardCheck,
  IconSchool, IconInfoCircle, IconDroplet, IconBuildingBridge,
  IconTornado, IconCalendar, IconShield, IconAmbulance, IconConfetti,
};

export default function IncidentDashboard() {
  const navigate = useNavigate();
  const { incidents, loadIncidents } = useAuthStore();

  useEffect(() => { loadIncidents(); }, []);

  const activeIncidents = useMemo(() => incidents.filter(i => i.status === 'active'), [incidents]);
  const monitoringIncidents = useMemo(() => incidents.filter(i => i.status === 'monitoring'), [incidents]);
  const closedIncidents = useMemo(() => incidents.filter(i => i.status === 'closed'), [incidents]);

  return (
    <div>
      <Group justify="space-between" mb="md">
        <Title order={3} c="black">Incident Hub</Title>
        <Button size="xs" leftSection={<IconPlus size={14} />}
          onClick={() => navigate('/incidents/new')}>New Incident</Button>
      </Group>

      {/* Stat Cards */}
      <SimpleGrid cols={{ base: 2, sm: 4 }} mb="lg">
        <Paper p="md" radius="sm" withBorder>
          <Group gap="xs" mb={4}>
            <IconAlertTriangle size={16} style={{ color: STATUS_COLORS.active }} />
            <Text size="xs" c="dimmed">Active</Text>
          </Group>
          <Text fz={28} fw={700} style={{ color: STATUS_COLORS.active }}>{activeIncidents.length}</Text>
        </Paper>
        <Paper p="md" radius="sm" withBorder>
          <Group gap="xs" mb={4}>
            <IconEye size={16} style={{ color: STATUS_COLORS.monitoring }} />
            <Text size="xs" c="dimmed">Monitoring</Text>
          </Group>
          <Text fz={28} fw={700} style={{ color: STATUS_COLORS.monitoring }}>{monitoringIncidents.length}</Text>
        </Paper>
        <Paper p="md" radius="sm" withBorder>
          <Group gap="xs" mb={4}>
            <IconShield size={16} style={{ color: STATUS_COLORS.closed }} />
            <Text size="xs" c="dimmed">Closed</Text>
          </Group>
          <Text fz={28} fw={700} style={{ color: STATUS_COLORS.closed }}>{closedIncidents.length}</Text>
        </Paper>
        <Paper p="md" radius="sm" withBorder>
          <Group gap="xs" mb={4}>
            <IconArchive size={16} style={{ color: '#7B7D7F' }} />
            <Text size="xs" c="dimmed">Total</Text>
          </Group>
          <Text fz={28} fw={700}>{incidents.length}</Text>
        </Paper>
      </SimpleGrid>

      {/* Active Incidents */}
      <Group justify="space-between" mb="sm">
        <Text fw={600} size="sm">Active Incidents</Text>
        <Badge size="sm" variant="light" color="#C68700">{activeIncidents.length}</Badge>
      </Group>
      {activeIncidents.length === 0 ? (
        <Paper p="xl" radius="sm" withBorder mb="lg">
          <Text ta="center" c="dimmed">No active incidents. Create one to get started.</Text>
        </Paper>
      ) : (
        <Stack gap="xs" mb="lg">
          {activeIncidents.slice(0, 8).map(inc => {
            const meta = INCIDENT_TYPE_META[(inc.incidentType || '').toLowerCase()] || INCIDENT_TYPE_META.other;
            const Icon = ICON_MAP[meta.icon] || IconInfoCircle;
            return (
              <Paper key={inc.id} p="sm" radius="sm" withBorder
                style={{ cursor: 'pointer' }} onClick={() => navigate(`/incidents/${inc.id}`)}>
                <Group justify="space-between">
                  <Group gap="sm">
                    <div style={{
                      width: 36, height: 36, borderRadius: 4,
                      background: meta.color + '18', display: 'flex',
                      alignItems: 'center', justifyContent: 'center',
                    }}>
                      <Icon size={18} color={meta.color} />
                    </div>
                    <div>
                      <Text size="sm" fw={600}>{inc.name}</Text>
                      <Text size="xs" c="dimmed">
                        {meta.label} · {inc.location || 'No location'} · {inc.commander ? `IC: ${inc.commander}` : 'No IC'}
                      </Text>
                    </div>
                  </Group>
                  <Badge variant="light" style={{
                    backgroundColor: STATUS_COLORS[inc.status] + '18',
                    color: STATUS_COLORS[inc.status],
                  }}>{inc.status}</Badge>
                </Group>
              </Paper>
            );
          })}
          {activeIncidents.length > 8 && (
            <Button variant="subtle" size="xs" rightSection={<IconArrowRight size={14} />}
              onClick={() => navigate('/incidents/list')}>View All</Button>
          )}
        </Stack>
      )}

      {/* Monitoring */}
      {monitoringIncidents.length > 0 && (
        <>
          <Group justify="space-between" mb="sm">
            <Text fw={600} size="sm">Monitoring</Text>
            <Badge size="sm" variant="light" color="#03AC9E">{monitoringIncidents.length}</Badge>
          </Group>
          <Stack gap="xs" mb="lg">
            {monitoringIncidents.slice(0, 5).map(inc => {
              const meta = INCIDENT_TYPE_META[(inc.incidentType || '').toLowerCase()] || INCIDENT_TYPE_META.other;
              const Icon = ICON_MAP[meta.icon] || IconInfoCircle;
              return (
                <Paper key={inc.id} p="sm" radius="sm" withBorder
                  style={{ cursor: 'pointer' }} onClick={() => navigate(`/incidents/${inc.id}`)}>
                  <Group justify="space-between">
                    <Group gap="sm">
                      <Icon size={16} color={meta.color} />
                      <div>
                        <Text size="sm" fw={600}>{inc.name}</Text>
                        <Text size="xs" c="dimmed">{meta.label} · {inc.location || '—'}</Text>
                      </div>
                    </Group>
                    <Badge variant="light" size="sm" style={{
                      backgroundColor: STATUS_COLORS.monitoring + '18',
                      color: STATUS_COLORS.monitoring,
                    }}>monitoring</Badge>
                  </Group>
                </Paper>
              );
            })}
          </Stack>
        </>
      )}
    </div>
  );
}
